import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY, formatDate } from "@/lib/format";
import { Loader2, Users, Building, TrendingUp, Power, Trash2, FileText, Receipt, ShieldCheck, UserPlus, Copy, Check, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent = "emerald", testid }) => {
  const map = {
    emerald: "bg-emerald-50 text-emerald-900",
    orange: "bg-orange-50 text-orange-700",
    stone: "bg-stone-100 text-stone-700",
    amber: "bg-amber-50 text-amber-800",
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${map[accent]}`}>
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
      <div className="mt-4 text-xs uppercase tracking-[0.1em] font-semibold text-stone-500">{label}</div>
      <div className="mt-1 font-numbers font-bold text-2xl text-stone-900">{value}</div>
    </div>
  );
};

export default function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regOpen, setRegOpen] = useState(false);
  const [mode, setMode] = useState("company"); // company | existing
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reg, setReg] = useState({ company_name: "", email: "", name: "", role: "staff", tenant_id: "", password: "", is_platform_admin: false });
  const [result, setResult] = useState(null); // {email, password, ...}
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "読み込みエラー");
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const toggleActive = async (uid) => {
    try {
      await api.patch(`/admin/users/${uid}/toggle-active`);
      toast.success("ステータスを更新しました");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteUser = async (uid, email) => {
    if (!confirm(`本当に ${email} を削除しますか？\n紐づく全データ(請求書・経費・仕訳など)も削除されます。`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("ユーザーを削除しました");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openRegister = async () => {
    setReg({ company_name: "", email: "", name: "", role: "staff", tenant_id: "", password: "", is_platform_admin: false });
    setResult(null); setMode("company"); setRegOpen(true);
    try {
      const [t, r] = await Promise.all([api.get("/admin/tenants"), api.get("/admin/assignable-roles")]);
      setTenants(t.data); setRoles(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitRegister = async () => {
    setSaving(true);
    try {
      let res;
      if (mode === "company") {
        if (!reg.company_name || !reg.email || !reg.name) { toast.error("会社名・氏名・メールは必須です"); setSaving(false); return; }
        res = await api.post("/admin/users/create-company", {
          company_name: reg.company_name, admin_email: reg.email, admin_name: reg.name,
          account_type: "sme", password: reg.password || null, is_platform_admin: reg.is_platform_admin,
        });
      } else {
        if (!reg.tenant_id || !reg.email || !reg.name) { toast.error("会社・氏名・メールは必須です"); setSaving(false); return; }
        res = await api.post("/admin/users/add-to-company", {
          tenant_id: reg.tenant_id, email: reg.email, name: reg.name, role: reg.role,
          password: reg.password || null, is_platform_admin: reg.is_platform_admin,
        });
      }
      setResult(res.data);
      toast.success("ユーザーを登録しました");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const copyPw = async () => {
    if (!result?.password) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(result.password);
      } else {
        const ta = document.createElement("textarea");
        ta.value = result.password; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); ta.remove();
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("コピーできませんでした。手動で選択してコピーしてください");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-900" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="superadmin-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-emerald-800" strokeWidth={2} />
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">スーパー管理者</div>
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-stone-900">プラットフォーム管理</h1>
          <p className="mt-1 text-sm text-stone-500">全ユーザー・全アカウントの統計と管理</p>
        </div>
        <button onClick={openRegister} data-testid="admin-register-user-button"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-950">
          <UserPlus className="h-4 w-4" /> ユーザー登録
        </button>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="admin-stat-users" icon={Users} label="登録ユーザー数" value={stats?.total_users || 0} accent="emerald" />
        <StatCard testid="admin-stat-active" icon={Power} label="有効ユーザー" value={`${stats?.active_users || 0} / ${stats?.total_users || 0}`} accent="amber" />
        <StatCard testid="admin-stat-new" icon={TrendingUp} label="直近30日 新規" value={`+${stats?.new_users_30d || 0}`} accent="emerald" />
        <StatCard testid="admin-stat-companies" icon={Building} label="個人 / 法人" value={`${stats?.individual_users || 0} / ${stats?.sme_users || 0}`} accent="stone" />
        <StatCard testid="admin-stat-invoices" icon={FileText} label="請求書 (累計)" value={stats?.total_invoices || 0} accent="emerald" />
        <StatCard testid="admin-stat-expenses-count" icon={Receipt} label="経費レコード" value={stats?.total_expense_records || 0} accent="orange" />
        <StatCard testid="admin-stat-revenue" icon={TrendingUp} label="累計入金額" value={formatJPY(stats?.platform_revenue_paid)} accent="emerald" />
        <StatCard testid="admin-stat-expense-total" icon={Receipt} label="累計経費額" value={formatJPY(stats?.platform_expense_total)} accent="orange" />
      </div>

      {/* User Table */}
      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="p-6 border-b border-stone-200">
          <h2 className="font-display font-semibold text-lg text-stone-900">ユーザー一覧</h2>
          <p className="text-xs text-stone-500 mt-0.5">合計 {users.length} 名</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-stone-500 uppercase tracking-wider bg-stone-50/50 border-b border-stone-200">
                <th className="px-6 py-3 text-left font-semibold">名前</th>
                <th className="px-6 py-3 text-left font-semibold">メール</th>
                <th className="px-6 py-3 text-left font-semibold">事業形態</th>
                <th className="px-6 py-3 text-left font-semibold">役割</th>
                <th className="px-6 py-3 text-right font-semibold">請求書</th>
                <th className="px-6 py-3 text-right font-semibold">経費</th>
                <th className="px-6 py-3 text-left font-semibold">登録日</th>
                <th className="px-6 py-3 text-left font-semibold">状態</th>
                <th className="px-6 py-3 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium text-stone-900">{u.name}</div>
                    {u.company_name && <div className="text-xs text-stone-500">{u.company_name}</div>}
                  </td>
                  <td className="px-6 py-3 text-stone-700">{u.email}</td>
                  <td className="px-6 py-3">
                    <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-xs font-medium text-stone-700">
                      {u.account_type === "individual" ? "個人" : "法人"}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                        <ShieldCheck className="h-3 w-3" />管理者
                      </span>
                    ) : (
                      <span className="text-xs text-stone-500">ユーザー</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right font-numbers text-stone-700">{u.invoices_count}</td>
                  <td className="px-6 py-3 text-right font-numbers text-stone-700">{u.expenses_count}</td>
                  <td className="px-6 py-3 text-stone-500">{formatDate(u.created_at)}</td>
                  <td className="px-6 py-3">
                    {u.is_active === false ? (
                      <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700" data-testid={`admin-user-inactive-${u.id}`}>無効</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800" data-testid={`admin-user-active-${u.id}`}>有効</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {u.role !== "admin" && (
                        <>
                          <button
                            onClick={() => toggleActive(u.id)}
                            data-testid={`admin-toggle-${u.id}`}
                            className="h-8 px-2.5 rounded-md hover:bg-stone-100 inline-flex items-center gap-1 text-xs font-medium text-stone-700"
                            title="有効/無効を切替"
                          >
                            <Power className="h-3.5 w-3.5" /> {u.is_active === false ? "有効化" : "無効化"}
                          </button>
                          <button
                            onClick={() => deleteUser(u.id, u.email)}
                            data-testid={`admin-delete-${u.id}`}
                            className="h-8 w-8 rounded-md hover:bg-orange-50 inline-flex items-center justify-center text-stone-400 hover:text-orange-600"
                            title="削除"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ユーザー登録ダイアログ */}
      <Dialog open={regOpen} onOpenChange={(o) => { setRegOpen(o); if (!o) setResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="admin-register-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-emerald-800" /> ユーザー登録</DialogTitle>
            <DialogDescription>新規会社の管理者、または既存会社へのユーザーを登録します。</DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-4" data-testid="admin-register-result">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                <div className="flex items-center gap-2 text-emerald-800 font-medium text-sm"><Check className="h-4 w-4" /> 登録が完了しました</div>
                <div className="mt-2 text-sm text-stone-700">{result.email}{result.company_name ? `（${result.company_name}）` : ""}</div>
                <div className="text-xs text-stone-500">{result.role_label || "契約企業管理者"}</div>
              </div>
              {result.password ? (
                <div>
                  <Label className="text-xs flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> 初期パスワード（この画面でのみ表示）</Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-stone-100 border border-stone-200 px-3 py-2 font-mono text-sm select-all" data-testid="admin-register-password">{result.password}</code>
                    <button onClick={copyPw} className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-3 py-2 text-sm hover:bg-stone-50" data-testid="admin-register-copy">
                      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}{copied ? "コピー済" : "コピー"}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-amber-600">※ 本人へ安全な方法で通知してください。再表示はできません。</p>
                </div>
              ) : (
                <p className="text-sm text-stone-600">既存ユーザーを会社に追加しました（パスワードは変更されていません）。</p>
              )}
              <DialogFooter>
                <button onClick={() => { setResult(null); }} className="rounded-md border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50" data-testid="admin-register-another">続けて登録</button>
                <button onClick={() => setRegOpen(false)} className="rounded-md bg-emerald-900 px-5 py-2 text-sm text-white hover:bg-emerald-950">閉じる</button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setMode("company")} data-testid="admin-mode-company"
                  className={`rounded-md border px-3 py-2 text-sm ${mode === "company" ? "border-emerald-700 bg-emerald-50 text-emerald-900 font-medium" : "border-stone-200 text-stone-600"}`}>新規会社＋管理者</button>
                <button onClick={() => setMode("existing")} data-testid="admin-mode-existing"
                  className={`rounded-md border px-3 py-2 text-sm ${mode === "existing" ? "border-emerald-700 bg-emerald-50 text-emerald-900 font-medium" : "border-stone-200 text-stone-600"}`}>既存会社にユーザー追加</button>
              </div>

              {mode === "company" ? (
                <div><Label className="text-xs">会社名</Label><Input value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} className="mt-1" data-testid="admin-reg-company" placeholder="例: 〇〇株式会社" /></div>
              ) : (
                <div><Label className="text-xs">所属会社</Label>
                  <Select value={reg.tenant_id} onValueChange={(v) => setReg({ ...reg, tenant_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="admin-reg-tenant"><SelectValue placeholder="会社を選択" /></SelectTrigger>
                    <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}（{t.members}名）</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">氏名</Label><Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className="mt-1" data-testid="admin-reg-name" /></div>
                <div><Label className="text-xs">メールアドレス</Label><Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className="mt-1" data-testid="admin-reg-email" /></div>
              </div>
              {mode === "existing" && (
                <div><Label className="text-xs">権限</Label>
                  <Select value={reg.role} onValueChange={(v) => setReg({ ...reg, role: v })}>
                    <SelectTrigger className="mt-1" data-testid="admin-reg-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className="text-xs">初期パスワード（空欄で自動生成）</Label><Input value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="mt-1" data-testid="admin-reg-password" placeholder="自動生成する場合は空欄" /></div>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" checked={reg.is_platform_admin} onChange={(e) => setReg({ ...reg, is_platform_admin: e.target.checked })} data-testid="admin-reg-platform" />
                運営会社管理者（プラットフォーム管理者）権限を付与
              </label>
              <DialogFooter>
                <button onClick={() => setRegOpen(false)} className="rounded-md border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50">キャンセル</button>
                <button onClick={submitRegister} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-5 py-2 text-sm text-white hover:bg-emerald-950 disabled:opacity-60" data-testid="admin-reg-submit">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} 登録する
                </button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
